/**
 * Client helper for /api/chat/marshmallow/generate-plugin.
 * Starts an async job (avoids CloudFront 504) and polls until the full
 * research-backed OpenAPI draft is ready.
 */

type AuthenticatedFetch = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>

export type GeneratePluginClientResult = Record<string, unknown>

type JobPayload = {
  error?: string | null
  id?: string
  result?: GeneratePluginClientResult | null
  status?: string
}

const POLL_MS = 2000
const MAX_WAIT_MS = 8 * 60 * 1000

async function readJson(response: Response): Promise<JobPayload & GeneratePluginClientResult> {
  return (await response.json().catch(() => ({}))) as JobPayload & GeneratePluginClientResult
}

function isTerminalJob(status: string | undefined) {
  return status === 'completed' || status === 'failed'
}

/**
 * Generate a custom integration plugin from natural language.
 * Uses the async job API by default so long doc research can finish.
 */
export async function runGeneratePluginRequest(
  authenticatedFetch: AuthenticatedFetch,
  userInput: string
): Promise<GeneratePluginClientResult> {
  const start = await authenticatedFetch('/api/chat/marshmallow/generate-plugin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userInput })
  })

  const startBody = await readJson(start)

  // Legacy sync response (plugin object with name/schema) — keep working.
  if (start.ok && startBody.name && startBody.schema && !startBody.status) {
    return startBody
  }

  if (start.status !== 202 && !start.ok) {
    throw new Error(
      (typeof startBody.error === 'string' && startBody.error) ||
        (start.status === 504
          ? 'Generation timed out at the gateway. Try again.'
          : 'Failed to generate integration')
    )
  }

  const jobId = typeof startBody.id === 'string' ? startBody.id : ''
  if (!jobId) {
    throw new Error('Plugin generation did not return a job id')
  }

  const deadline = Date.now() + MAX_WAIT_MS
  while (Date.now() < deadline) {
    await new Promise(resolve => setTimeout(resolve, POLL_MS))
    const poll = await authenticatedFetch(
      `/api/chat/marshmallow/generate-plugin?jobId=${encodeURIComponent(jobId)}`,
      { method: 'GET', cache: 'no-store' }
    )
    const body = await readJson(poll)
    if (!poll.ok) {
      throw new Error(
        (typeof body.error === 'string' && body.error) || 'Failed to poll plugin generation'
      )
    }
    if (!isTerminalJob(body.status)) continue
    if (body.status === 'failed') {
      throw new Error(body.error || 'Plugin generation failed')
    }
    if (body.result && typeof body.result === 'object') {
      return body.result
    }
    throw new Error('Plugin generation completed without a result')
  }

  throw new Error('Plugin generation timed out waiting for the research job')
}
